import { z } from "zod";
import {
  GuestFilingErrorCodeSchema,
  GuestFilingKindSchema,
  GuestFilingStatusSchema,
  RegistrationBucketSchema,
} from "../registration";

// API-boundary contracts for the guest-registration filing engine.
// Plain zod (no drizzle / pg-core) so the mobile bundle stays decoupled from
// the DB package. Both the API route handlers and the mobile api/ client import
// these, so request and response shapes can't drift.

// The host-facing view of a filing. Timestamps are strings: Next.js serializes
// the DB Date to an ISO string in JSON. This mirrors the columns synced to the
// staff app — it deliberately omits the dedup key, in-flight claim, and the raw
// payload/response (those never leave the server).
export const GuestFilingSchema = z.object({
  attemptCount: z.number(),
  bucket: RegistrationBucketSchema,
  confirmedAt: z.string().nullable(),
  createdAt: z.string(),
  deadlineAt: z.string().nullable(),
  dueAt: z.string().nullable(),
  escalatedAt: z.string().nullable(),
  guestId: z.string(),
  id: z.string(),
  kind: GuestFilingKindSchema,
  lastErrorCode: GuestFilingErrorCodeSchema.nullable(),
  lastErrorMessage: z.string().nullable(),
  propertyId: z.string(),
  providerRef: z.string().nullable(),
  receiptId: z.string().nullable(),
  reservationId: z.string(),
  status: GuestFilingStatusSchema,
  submittedAt: z.string().nullable(),
  system: z.string(),
  updatedAt: z.string(),
  withdrawnAt: z.string().nullable(),
});
export type GuestFiling = z.infer<typeof GuestFilingSchema>;

// Host action: resubmit a filing that stalled on a hard error after the host
// has fixed the underlying problem (missing data, wrong credentials).
export const RetryFilingInputSchema = z.object({
  reason: z.string().trim().max(500).optional(),
});

// Host action: withdraw a filing the authority has already accepted, where the
// register models that as a state on the record (AJPES's `storno`). Destructive
// and not undoable from the product.
//
// `reason` is accepted and recorded on the filing's audit event. Optional: no
// host surface collects one yet, and refusing the request for want of a note
// would block the action rather than improve the record.
export const WithdrawFilingInputSchema = z.object({
  reason: z.string().trim().max(500).optional(),
});
export type WithdrawFilingInput = z.infer<typeof WithdrawFilingInputSchema>;

// Host action behind "Review details": a staff member corrects a guest's
// registration data, which is saved and the guest's due filings resubmitted
// inline. The outcome mirrors the retry: what the authority said this time.
// `errorCode` disambiguates the one outcome that hides two opposite stories:
// `action_needed` covers both "an authority refused this record" and "no
// connector exists for this system, so nobody ever saw it". Present only when
// the outcome is `action_needed`; nullable because a filing can need a host
// without any connector error behind it (missing data is decided before a
// connector runs).
export const SaveRegistrationResponseSchema = z.object({
  errorCode: z.string().nullable(),
  ok: z.literal(true),
  outcome: z.enum(["filed", "submitted", "action_needed", "none"]),
});
